import {isValidElement} from 'react';
import type {ComponentType, ReactElement} from 'react';

const SAIL_UI_COMPONENT = Symbol.for('sail-ui-component');

type WithSailSymbol<P> = {
  [SAIL_UI_COMPONENT]?: ComponentType<P>;
};

function isObject(val: unknown): val is Record<string, unknown> {
  return typeof val === 'object' && val !== null && !Array.isArray(val);
}

function isComponentWithSailSymbol<P>(
  comp: unknown,
): comp is WithSailSymbol<P> {
  return (
    (isObject(comp) || typeof comp === 'function') && SAIL_UI_COMPONENT in comp
  );
}

/**
 * Checks if a React element is of the expected component type.
 *
 * Supports both:
 * - Direct type comparison (for native Sail components)
 * - Symbol-based type marking (for wrapped components)
 */
export function isComponentType<P>(
  element: unknown,
  expectedType: ComponentType<P>,
): element is ReactElement<P> {
  if (!isValidElement(element)) {
    return false;
  }
  if (element.type === expectedType) {
    return true;
  }
  if (!isComponentWithSailSymbol(element.type)) {
    return false;
  }
  return element.type[SAIL_UI_COMPONENT] === expectedType;
}

/**
 * Marks a wrapper component as representing a specific native component type.
 * This allows the wrapper to pass type checks in components that filter
 * children by type (e.g., ButtonGroup, TaskList).
 */
export function markAsSailComponent<P, T extends P>(
  Component: ComponentType<T>,
  SailComponent: ComponentType<P>,
): ComponentType<T> {
  const Wrapper = Component as WithSailSymbol<P>;
  Wrapper[SAIL_UI_COMPONENT] = SailComponent;

  return Component;
}
